import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import {
  api,
  type ClosedLate,
  type ClosedReport as ClosedReportData,
  type ReportScope,
} from "../../../lib/api";
import { useIsDark } from "../../../lib/theme";
import {
  withCardFilters,
  type DashboardLayout,
} from "../../../lib/dashboard-layout";
import { queryKeys } from "../../../lib/queryKeys";
import { DASHBOARD_STALE_MS } from "../../../lib/dashboard-refresh";
import { formatInZone } from "../../../lib/time";
import { cn } from "../../ui/cn";
import { Delta, rangeIsReady, toRangeValue, type RangeValue } from "../RangeFilter";
import { CardChart, ReportCard } from "../card/ReportCard";
import { TicketCount } from "../card/TicketCount";
import { CATEGORY_CHARTS, type ChartType } from "../card/chart-types";

/**
 * What got finished, and how much of it was finished on time.
 *
 * A ticket counts as late if *any* of its measured response cycles breached.
 * Answering promptly six times does not undo having left somebody a week the
 * first time, and a per-ticket figure that averaged its cycles would let a
 * chatty thread bury one bad wait.
 *
 * Tickets whose cycles were all on untimed pipelines are reported separately
 * rather than counted as on time. Nobody promised anything about them, and
 * folding them into the numerator would flatter the rate with work that was
 * never measured.
 */

/**
 * Green for met, red for missed — not whatever the generator produces next.
 *
 * This is the one card whose categories carry a meaning the palette cannot
 * know. A generated scheme happily paints "on time" pink and "late" green, and
 * a reader glancing at the donut takes the wrong impression before reading a
 * single label. So these start from the same SLA tokens the ticket list uses,
 * and remain overridable like anything else.
 *
 * Literal hex rather than the CSS variables, because these end up in an SVG
 * `fill` that recharts wants as a value; the pair is picked per theme for the
 * same reason the generated colours are.
 */
const SLA_COLORS = {
  light: { onTime: "#047857", late: "#be123c" },
  dark: { onTime: "#34d399", late: "#fb7185" },
};

export function ClosedReport({
  layout,
  scope: pageScope,
  isManager,
}: {
  layout: DashboardLayout;
  /** Only a manager is offered the Agent filter; the server enforces it. */
  isManager: boolean;
  scope: ReportScope;
}) {
  const dark = useIsDark();
  const tones = SLA_COLORS[dark ? "dark" : "light"];
  const stored = layout.card("closed");
  /* The card's own Pipeline and Agent filters, folded into the page's
     scope so they reach both the query and its key. */
  const scope = withCardFilters(pageScope, stored);
  const range = toRangeValue(stored.range, { preset: "this-month" });
  const ready = rangeIsReady(range);

  const report = useQuery({
    queryKey: queryKeys.dashboardReport("closed-report", range, scope),
    queryFn: () => api.closedReport(range, scope),
    enabled: ready,
    staleTime: DASHBOARD_STALE_MS,
  });

  const data = report.data;

  return (
    <ReportCard
      id="closed"
      title="Closed tickets & SLA split"
      subtitle="Against the same span in the previous period"
      layout={layout}
      offerAgents={isManager}
      /*
       * The category set, plus one option this card alone can honestly draw.
       * `line-split` needs two counted series over time, which is a shape only
       * this report produces -- see the note in `chart-types.ts` about why it
       * is named here rather than added to a shared preset.
       */
      charts={[...CATEGORY_CHARTS, "line-split"]}
      series={[
        { key: "On time", label: "On time", color: tones.onTime },
        { key: "Late", label: "Late", color: tones.late },
      ]}
      footnote={
        <>
          {data?.unmeasured ? (
            <p>
              {data.unmeasured} closed on a pipeline with no target, so outside
              the split rather than counted as on time.
            </p>
          ) : null}
          {data?.mergedCount ? (
            <p>{data.mergedCount} duplicates counted once.</p>
          ) : null}
          {data?.unrecorded ? (
            <p>
              {data.unrecorded} closed before resolution times were recorded, so
              they belong to no period at all.
            </p>
          ) : null}
        </>
      }
    >
      {({ chart, colorFor }) => {
        if (!ready) {
          return (
            <p className="py-6 text-center text-xs text-faint">
              Pick both dates.
            </p>
          );
        }

        return (
          <div className="space-y-2">
            <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
              <p className="text-xs text-muted">
                <span className="text-lg font-semibold tabular-nums text-content">
                  {(data?.closed ?? 0).toLocaleString()}
                </span>{" "}
                closed
              </p>
              <p className="flex items-baseline gap-2 text-xs text-muted">
                <span className="text-lg font-semibold tabular-nums text-content">
                  {data?.onTimeRate == null
                    ? "—"
                    : `${Math.round(data.onTimeRate * 100)}%`}
                </span>
                on time
                {/* Percentage points, because this is a rate. The velocity card
                    next door compares durations in per cent. */}
                <Delta points={data?.pointsDelta ?? null} />
              </p>
            </div>

            {chart === "table" ? (
              /*
               * The table gets a drill-down the charts do not. Expanding a
               * slice of a donut has nowhere to put a list of ticket numbers,
               * and a bar with a nested tree under it stops being a bar -- so
               * the affordance lives where rows already are and every other
               * view keeps the plain two-row summary.
               */
              <SplitTable
                onTime={data?.onTime ?? 0}
                late={data?.late ?? 0}
                colorFor={colorFor}
                range={range}
                scope={scope}
              />
            ) : (
              <CardChart
                chart={chart}
                {...seriesFor(chart, data)}
                colorBy={chart === "line-split" ? undefined : "row"}
                colorFor={colorFor}
                format={(value) =>
                  value === null ? "—" : value.toLocaleString()
                }
                emptyMessage={
                  report.isLoading
                    ? "Loading…"
                    : "Nothing was closed in this period."
                }
              />
            )}
          </div>
        );
      }}
    </ReportCard>
  );
}

/**
 * The rows and series a given view wants.
 *
 * Two shapes from one report. Every categorical view draws the period's two
 * totals; the line split draws the same two numbers per bucket, which is the
 * whole reason it is a separate option rather than "line" on the existing
 * data -- a line across two unordered categories means nothing, which is why
 * `CATEGORY_CHARTS` excludes it.
 *
 * The series keys are "On time" and "Late" in both, because those are what the
 * colour panel stores overrides against. A different key per view would give
 * the same card two sets of colours depending on how it was being drawn.
 */
function seriesFor(chart: ChartType, data: ClosedReportData | undefined) {
  if (chart !== "line-split") {
    return {
      rows: [
        { label: "On time", values: { count: data?.onTime ?? 0 } },
        { label: "Late", values: { count: data?.late ?? 0 } },
      ],
      series: [{ key: "count", label: "Tickets" }],
    };
  }

  return {
    rows: (data?.points ?? []).map((point) => ({
      label: bucketLabel(point.from, data?.granularity ?? "daily"),
      values: { "On time": point.onTime, Late: point.late },
    })),
    series: [
      { key: "On time", label: "On time" },
      { key: "Late", label: "Late" },
    ],
  };
}

/**
 * The two-row split, with the late half openable.
 *
 * Its own table rather than the shared `DataTable`, because a row that expands
 * into a grouped list of tickets is not a cell of numbers and bending the
 * shared renderer to it would put a drill-down concept into every card that
 * has no use for one.
 */
function SplitTable({
  onTime,
  late,
  colorFor,
  range,
  scope,
}: {
  onTime: number;
  late: number;
  colorFor: (key: string) => string;
  range: RangeValue;
  scope: ReportScope;
}) {
  const [open, setOpen] = useState(false);

  /* Fetched when the row is first opened and kept afterwards, so closing and
     reopening does not re-read a period that cannot have changed. */
  const detail = useQuery({
    queryKey: queryKeys.dashboardReport("closed-late", range, scope),
    queryFn: () => api.closedLate(range, scope),
    enabled: open && late > 0,
    staleTime: DASHBOARD_STALE_MS,
  });

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-xs">
        <thead>
          <tr className="border-b border-line text-left text-[11px] uppercase tracking-wide text-faint">
            <th className="px-2 py-1.5 font-medium">&nbsp;</th>
            <th className="px-2 py-1.5 text-right font-medium">Tickets</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="px-2 py-1.5 text-muted">
              <span className="inline-flex items-center gap-1.5">
                <Swatch color={colorFor("On time")} />
                On time
              </span>
            </td>
            <td className="px-2 py-1.5 text-right tabular-nums text-content">
              <TicketCount
                count={onTime}
                dimension="closed-on-time"
                label="Closed on time"
                range={range}
                scope={scope}
              />
            </td>
          </tr>

          <tr className="bg-surface-2/40">
            <td className="px-2 py-1.5 text-muted">
              {/* No arrow at all when there is nothing behind it, rather than
                  a control that opens onto an empty list. */}
              {late === 0 ? (
                <span className="inline-flex items-center gap-1.5 pl-[1.125rem]">
                  <Swatch color={colorFor("Late")} />
                  Late
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => setOpen(!open)}
                  aria-expanded={open}
                  className="inline-flex items-center gap-1.5 text-left hover:text-content"
                >
                  <span
                    aria-hidden
                    className={cn(
                      "text-[9px] text-faint transition-transform",
                      open && "rotate-90",
                    )}
                  >
                    ▶
                  </span>
                  <Swatch color={colorFor("Late")} />
                  Late
                </button>
              )}
            </td>
            {/* Plain text, and the one count on the dashboard that is: the
                arrow beside it already opens a richer answer -- grouped by
                where it went wrong, which a flat list is not -- and a row
                offering two different drill-downs is a row that makes the
                reader choose before they know the difference. */}
            <td className="px-2 py-1.5 text-right tabular-nums text-content">
              {late.toLocaleString()}
            </td>
          </tr>

          {open && late > 0 ? (
            <tr>
              <td colSpan={2} className="px-2 pb-2">
                {detail.isLoading ? (
                  <p className="py-2 text-[11px] text-faint">Loading…</p>
                ) : (
                  <LateGroups data={detail.data} />
                )}
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}

/** How many tickets a subgroup shows before it has to be asked for more. */
const PER_GROUP = 3;

function LateGroups({ data }: { data: ClosedLate | undefined }) {
  const groups = data?.groups ?? [];

  if (groups.length === 0) {
    return <p className="py-2 text-[11px] text-faint">Nothing to show.</p>;
  }

  return (
    <div className="space-y-2 border-l-2 border-line pl-3">
      {groups.map((group) => (
        <LateGroup key={group.key} group={group} />
      ))}

      {data?.truncated ? (
        <p className="text-[11px] text-faint">
          Only the most recent {groups.reduce((sum, g) => sum + g.count, 0)} are
          listed — narrow the period to see the rest.
        </p>
      ) : null}
    </div>
  );
}

function LateGroup({ group }: { group: ClosedLate["groups"][number] }) {
  const [all, setAll] = useState(false);
  const shown = all ? group.tickets : group.tickets.slice(0, PER_GROUP);
  const hidden = group.tickets.length - shown.length;

  return (
    <div>
      <p className="text-[11px] font-medium text-content">
        {group.key}: {group.count.toLocaleString()}
      </p>

      <ul className="mt-0.5 space-y-0.5">
        {shown.map((ticket) => (
          <li
            key={ticket.ticketId}
            /* One line, whatever the route. A path that wraps turns a list of
               five tickets into a paragraph. */
            className="flex items-baseline gap-1.5 text-[11px]"
          >
            <span className="min-w-0 flex-1 truncate text-muted">
              <span className="text-content">#{ticket.ticketId}</span>
              {ticket.routePath ? `: ${ticket.routePath}` : null}
            </span>
            <Link
              to={`/v/open/${ticket.ticketId}`}
              className="shrink-0 text-brand-600 hover:underline dark:text-brand-400"
            >
              View ticket ↗
            </Link>
          </li>
        ))}
      </ul>

      {group.tickets.length > PER_GROUP ? (
        <button
          type="button"
          onClick={() => setAll(!all)}
          className="mt-0.5 text-[11px] text-brand-600 hover:underline dark:text-brand-400"
        >
          {all ? "Show less" : `Show ${hidden} more`}
        </button>
      ) : null}
    </div>
  );
}

function Swatch({ color }: { color: string }) {
  return (
    <span
      aria-hidden
      className="size-1.5 shrink-0 rounded-full"
      style={{ backgroundColor: color }}
    />
  );
}

/** A bucket's x-axis label, in London time like every other date on the desk. */
function bucketLabel(iso: string, granularity: string): string {
  const options: Intl.DateTimeFormatOptions =
    granularity === "hourly"
      ? { hour: "2-digit", minute: "2-digit" }
      : granularity === "monthly"
        ? { month: "short", year: "2-digit" }
        : { day: "numeric", month: "short" };

  return formatInZone(iso, options);
}
